import React, { useCallback, useMemo, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import {
  Box,
  IconButton,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ListCard from "../components/ListCard";
import ItemDialog from "../components/ItemDialog";
import { removeItem, upsertItem } from "../../hooks/useTrustCenterAdmin";
import { includesQuery } from "../../../Organization/CodeSecurity/codeSecurityShared";
import usePresentFilter from "../../hooks/usePresentFilter";
import VendorLogo from "../../components/VendorLogo";
import {
  DATA_LOCATION_SUGGESTIONS,
  MAX_DATA_LOCATIONS,
  formatLocations,
} from "../../data/subprocessors";
import { LOGO_HINT } from "../../data/uploadRules";
import { uploadSubprocessorLogo } from "../../../../apis/trust-center-apis/trustCenterApis";

const FILTERS = {
  Shown: (row) => row.isVisible,
  Hidden: (row) => !row.isVisible,
  Integrations: (row) => row.source === "integration",
  "Added manually": (row) => row.source === "manual",
};

const columns = [
  { label: "Vendor", field: "name", width: "28%" },
  { label: "What they do", field: "purpose", width: "24%" },
  { label: "Data stored in", field: "dataLocations", width: "24%" },
  { label: "Show on page", field: "isVisible", width: "14%", align: "center" },
  { label: "Actions", field: "actions", width: "10%", align: "center" },
];

const LOCATION_FIELD = {
  name: "dataLocations",
  label: "Where they store data",
  type: "tags",
  options: DATA_LOCATION_SUGGESTIONS,
  max: MAX_DATA_LOCATIONS,
  helperText:
    "Countries or regions where they store or process your customers' data. Pick one or type your own and press Enter.",
};

const MANUAL_FIELDS = [
  { name: "name", label: "Company name", type: "text", required: true },
  {
    name: "purpose",
    label: "What they do for you",
    type: "text",
    required: true,
    helperText: "For example: Payments, Customer support, Error monitoring.",
  },
  LOCATION_FIELD,
];

// The name of a connected integration comes from Regodit and cannot change.
const INTEGRATION_FIELDS = [MANUAL_FIELDS[1], LOCATION_FIELD];

// New vendors start hidden, like integrations: the admin switches them on.
const EMPTY_MANUAL = {
  name: "",
  purpose: "",
  isVisible: false,
  dataLocations: [],
  logoPath: "",
};

const SubprocessorsTab = ({
  subprocessors,
  integrationSubprocessors,
  onChange,
}) => {
  const theme = useTheme();
  const { getAccessTokenSilently, getIdTokenClaims } = useAuth0();
  const uploadLogo = useCallback(
    async (file) => {
      const accessToken = await getAccessTokenSilently();
      const idToken = (await getIdTokenClaims()).__raw;
      const saved = await uploadSubprocessorLogo(file, { accessToken, idToken });
      return saved.logoPath;
    },
    [getAccessTokenSilently, getIdTokenClaims]
  );
  const manualFields = useMemo(
    () => [
      {
        name: "logoPath",
        label: "Logo",
        type: "logo",
        helperText: `${LOGO_HINT}. Optional.`,
        upload: uploadLogo,
      },
      ...MANUAL_FIELDS,
    ],
    [uploadLogo]
  );
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  // Connected integrations come from the Trust Center API: those with an
  // active, not deleted row in regodit-customers-integrations (several
  // accounts of the same tool count once), with the admin's saved choices.
  // New ones stay hidden until the admin shows them.
  const integrationRows = useMemo(
    () =>
      Object.entries(integrationSubprocessors).map(([key, saved]) => ({
        id: `integration-${key}`,
        rowId: `integration-${key}`,
        key,
        source: "integration",
        name: saved.name || key,
        purpose: saved.purpose || "",
        isVisible: !!saved.isVisible,
        logoPath: saved.logoPath || null,
        dataLocations: saved.dataLocations || [],
      })),
    [integrationSubprocessors]
  );

  const allRows = useMemo(
    () =>
      [
        ...integrationRows,
        ...subprocessors.map((sub) => ({
          ...sub,
          rowId: `manual-${sub.id}`,
          source: "manual",
        })),
      ].sort((a, b) => a.name.localeCompare(b.name)),
    [integrationRows, subprocessors]
  );
  const {
    filter,
    setFilter,
    options: filterOptions,
  } = usePresentFilter(allRows, FILTERS);
  const rows = useMemo(
    () =>
      allRows
        .filter((row) => !filter || FILTERS[filter](row))
        .filter((row) =>
          includesQuery(
            search,
            row.name,
            row.purpose,
            formatLocations(row.dataLocations)
          )
        ),
    [allRows, search, filter]
  );

  const saveIntegration = (row, changes) =>
    onChange({
      integrationSubprocessors: {
        ...integrationSubprocessors,
        [row.key]: {
          name: row.name,
          purpose: row.purpose,
          isVisible: row.isVisible,
          logoPath: row.logoPath,
          dataLocations: row.dataLocations || [],
          ...changes,
        },
      },
    });

  const saveManual = (sub) => {
    const { rowId, source, ...clean } = sub;
    onChange({ subprocessors: upsertItem(subprocessors, clean) });
  };

  const setVisible = (row, isVisible) =>
    row.source === "integration"
      ? saveIntegration(row, { isVisible })
      : saveManual({ id: row.id, isVisible });

  const renderCell = (row, column) => {
    switch (column.field) {
      case "name":
        return (
          <Stack direction="row" spacing={1.25} alignItems="center">
            <VendorLogo
              name={row.name}
              logoPath={row.logoPath}
              size={32}
            />
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {row.name}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: theme.customColors.text.secondary }}
              >
                {row.source === "integration"
                  ? "Connected integration"
                  : "Added manually"}
              </Typography>
            </Box>
          </Stack>
        );
      case "dataLocations":
        return row.dataLocations?.length ? (
          <Typography variant="body2">
            {formatLocations(row.dataLocations)}
          </Typography>
        ) : (
          <Typography
            variant="body2"
            sx={{ color: theme.customColors.text.secondary }}
          >
            Not added
          </Typography>
        );
      case "isVisible":
        return (
          <Switch
            checked={row.isVisible}
            onChange={(e) => setVisible(row, e.target.checked)}
            inputProps={{ "aria-label": `Show ${row.name} on page` }}
          />
        );
      case "actions":
        return (
          <Stack direction="row" spacing={0.5} justifyContent="center">
            <Tooltip title="Edit subprocessor" arrow>
              <IconButton
                size="small"
                aria-label={`Edit ${row.name}`}
                onClick={() => setEditing(row)}
              >
                <ArrowForwardIosIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          </Stack>
        );
      default:
        return (
          <Typography variant="body2">{row[column.field] || "—"}</Typography>
        );
    }
  };

  const editingIntegration = editing?.source === "integration";

  return (
    <>
      <ListCard
        title="Subprocessors"
        subtitle="Companies that may process your customers' data. Your connected integrations are listed for you; add any others by hand and upload a logo if you have one. Only rows switched on appear on your Trust Center."
        addLabel="Add subprocessor"
        onAdd={() => setEditing(EMPTY_MANUAL)}
        searchQuery={search}
        onSearchChange={setSearch}
        filters={filterOptions}
        selectedFilter={filter}
        onFilterChange={setFilter}
        onClearFilters={() => {
          setSearch("");
          setFilter(null);
        }}
        countNoun="subprocessor"
        columns={columns}
        rows={rows}
        renderCell={renderCell}
        emptyText="No subprocessors match. Connect an integration or add one by hand."
      />
      <ItemDialog
        open={!!editing}
        title={
          editingIntegration
            ? `Edit ${editing.name}`
            : editing?.id
              ? "Edit subprocessor"
              : "Add subprocessor"
        }
        fields={editingIntegration ? INTEGRATION_FIELDS : manualFields}
        initialValues={editing}
        emptyValues={EMPTY_MANUAL}
        onClose={() => setEditing(null)}
        onSave={(values) =>
          editingIntegration
            ? saveIntegration(editing, {
                purpose: values.purpose.trim(),
                dataLocations: values.dataLocations || [],
              })
            : saveManual({
                ...values,
                name: values.name.trim(),
                purpose: values.purpose.trim(),
                logoPath: values.logoPath || null,
              })
        }
        // Integrations leave the list when they are disconnected; only
        // vendors added by hand can be removed.
        onRemove={
          editingIntegration
            ? null
            : (id) => onChange({ subprocessors: removeItem(subprocessors, id) })
        }
        removeHelperText="It will no longer appear on your Trust Center. You can add it again later."
      />
    </>
  );
};

export default SubprocessorsTab;
